import type { ReactElement, Ref } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Button, ButtonLink, cx } from '../ui';
import { ShowMeButton } from '../shell/ShowMeButton';
import type { HelpAnchorRef, HelpTopic } from '../shell/types';
import s from '../shell/HelpPanel.module.css';

/** Reader and editorial preview share one plain-text renderer. Preview never navigates. */
export function HelpArticle({
  topic,
  related = [],
  anchor,
  onShowMe,
  onOpen,
  preview = false,
  headingRef,
}: {
  topic: HelpTopic;
  related?: readonly Pick<HelpTopic, 'id' | 'title'>[];
  anchor?: HelpAnchorRef;
  onShowMe?: (anchor: HelpAnchorRef) => boolean;
  onOpen?: (id: string) => void;
  preview?: boolean;
  headingRef?: Ref<HTMLHeadingElement>;
}): ReactElement {
  return (
    <article className={s.article}>
      <p className={cx('eyebrow', s.articleCategory)}>{topic.category}</p>
      <h3 ref={headingRef} tabIndex={-1} className={cx('disp', s.articleTitle)}>
        {topic.title}
      </h3>
      <p className={s.articleBody}>{topic.summary}</p>
      {topic.body.map((paragraph, i) => (
        <p key={i} className={s.articleBody}>
          {paragraph}
        </p>
      ))}
      {topic.steps !== undefined && topic.steps.length > 0 && (
        <ol className={s.steps}>
          {topic.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}
      {topic.start !== undefined &&
        (preview ? (
          <Button isDisabled size="sm">
            {topic.start.label}
          </Button>
        ) : (
          <ButtonLink href={topic.start.path} variant="navy" size="sm" className={s.start}>
            {topic.start.label}
          </ButtonLink>
        ))}
      {anchor !== undefined &&
        (preview ? (
          <Button isDisabled size="sm">
            Show me {anchor.label}
          </Button>
        ) : (
          onShowMe !== undefined && (
            <p className={s.showMe}>
              <ShowMeButton
                key={anchor.id}
                anchor={anchor}
                showMe={onShowMe}
                className={s.linkButton}
                noteClassName={s.shownNote}
              />
            </p>
          )
        ))}
      {related.length > 0 && (
        <div className={s.related}>
          <h4 className={cx('eyebrow', s.groupLabel)}>Related</h4>
          <ul className={s.plainLinks}>
            {related.map((item) => (
              <li key={item.id}>
                <AriaButton
                  className={s.linkButton}
                  isDisabled={preview}
                  onPress={() => {
                    onOpen?.(item.id);
                  }}
                >
                  {item.title}
                </AriaButton>
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}
